import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Home, Box } from 'lucide-react';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';
import { formatWallDensity } from '../../utils/formatters';
import QuestionTooltip from '../shared/QuestionTooltip';
import { trackEvent } from '../../../../utils/analytics';

/**
 * Layout component
 * Fourth step in the wireless advisor form
 * Allows users to specify details about their home layout
 */
const Layout = () => {
  const { formData, updateFormData } = useWirelessAdvisor();
  
  // Handle floor count change
  const handleFloorCountChange = (count) => {
    // Update form data
    updateFormData({
      floors: count
    });
    
    // Track event
    trackEvent('wireless_advisor_floor_count_changed', {
      count
    });
  };
  
  // Handle wall density change
  const handleWallDensityChange = (density) => {
    // Update form data
    updateFormData({
      wallDensity: density
    });
    
    // Track event
    trackEvent('wireless_advisor_wall_density_changed', {
      density
    });
  };
  
  // Handle construction type change
  const handleConstructionTypeChange = (type) => {
    // Update form data
    updateFormData({
      constructionType: type
    });
    
    // Track event
    trackEvent('wireless_advisor_construction_type_changed', {
      type
    });
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Tell us about your home layout</h2>
        <QuestionTooltip 
          content="Details about your home's layout help us calculate signal propagation and potential interference."
          iconSize="md"
        />
      </div>
      
      <p className="text-gray-400 text-sm">
        These details help us understand how WiFi signals will travel through your home.
      </p>
      
      {/* Number of Floors */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-300">
          How many floors does your home have?
        </label>
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((count) => (
            <motion.button
              key={count}
              onClick={() => handleFloorCountChange(count)}
              className={`p-3 rounded-lg border-2 transition-all ${
                formData.floors === count
                  ? 'border-purple-600 bg-purple-600/10'
                  : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex flex-col items-center">
                <Layers className={`h-5 w-5 mb-1 ${formData.floors === count ? 'text-purple-400' : 'text-gray-400'}`} />
                <span className={`font-medium ${formData.floors === count ? 'text-purple-400' : 'text-white'}`}>
                  {count}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
      
      {/* Wall Density */}
      <div className="space-y-3">
        <div className="flex items-center">
          <label className="block text-sm font-medium text-gray-300 mr-2">
            How dense are the walls in your home?
          </label>
          <QuestionTooltip 
            content="Wall density affects how WiFi signals propagate. Dense walls with materials like concrete, brick, or metal significantly reduce signal strength."
            iconSize="sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((density) => (
            <motion.button
              key={density}
              onClick={() => handleWallDensityChange(density)}
              className={`p-4 rounded-lg border-2 transition-all ${
                formData.wallDensity === density
                  ? 'border-purple-600 bg-purple-600/10'
                  : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${formData.wallDensity === density ? 'bg-purple-600/20' : 'bg-gray-700'} mr-3`}>
                  <Box className={`h-5 w-5 ${formData.wallDensity === density ? 'text-purple-400' : 'text-gray-400'}`} />
                </div>
                <div className="text-left">
                  <span className={`font-medium ${formData.wallDensity === density ? 'text-purple-400' : 'text-white'}`}>
                    {formatWallDensity(density)}
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
      
      {/* Construction Type */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-300">
          What type of construction is your home?
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { id: 'wood', label: 'Wood Frame', description: 'Typical in many houses and apartments' },
            { id: 'concrete', label: 'Concrete/Brick', description: 'Common in older buildings and some modern construction' },
            { id: 'metal', label: 'Metal Frame', description: 'Found in some modern buildings and industrial conversions' },
            { id: 'mixed', label: 'Mixed Materials', description: 'Combination of different construction materials' }
          ].map((type) => (
            <motion.button
              key={type.id}
              onClick={() => handleConstructionTypeChange(type.id)}
              className={`p-4 rounded-lg border-2 transition-all ${
                formData.constructionType === type.id
                  ? 'border-purple-600 bg-purple-600/10'
                  : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${formData.constructionType === type.id ? 'bg-purple-600/20' : 'bg-gray-700'} mr-3`}>
                  <Home className={`h-5 w-5 ${formData.constructionType === type.id ? 'text-purple-400' : 'text-gray-400'}`} />
                </div>
                <div className="text-left">
                  <span className={`font-medium ${formData.constructionType === type.id ? 'text-purple-400' : 'text-white'}`}>
                    {type.label}
                  </span>
                  <p className="text-xs text-gray-400 mt-1">
                    {type.description}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
      
      {formData.floors && formData.wallDensity !== undefined && formData.constructionType && (
        <motion.div
          className="mt-6 p-4 bg-purple-600/10 border border-purple-600/20 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-lg font-medium text-purple-400 mb-2">
            Layout Summary
          </h3>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 mr-2"></span>
              <span>{formData.floors} {formData.floors === 1 ? 'floor' : 'floors'}</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 mr-2"></span>
              <span>Wall density: {formatWallDensity(formData.wallDensity)}</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 mr-2"></span>
              <span>Construction: {
                formData.constructionType === 'wood' ? 'Wood Frame' :
                formData.constructionType === 'concrete' ? 'Concrete/Brick' :
                formData.constructionType === 'metal' ? 'Metal Frame' : 'Mixed Materials'
              }</span>
            </li>
          </ul>
          
          {/* Construction impact note */}
          {formData.constructionType === 'concrete' || formData.constructionType === 'metal' ? (
            <p className="mt-3 text-sm text-amber-300">
              Note: {formData.constructionType === 'concrete' ? 'Concrete/brick' : 'Metal'} construction can significantly reduce WiFi signal strength. 
              We'll factor this into our recommendations.
            </p>
          ) : null}
        </motion.div>
      )}
    </div>
  );
};

export default Layout;